import React from "react";
import PropTypes from "prop-types";
import { useGuidance } from "./T2TGuidanceProvider";

/**
 * Layer 1 — First-Visit Welcome Screen
 * Full-screen overlay shown once on first visit to the Workflow Builder.
 * 4 entry points: Wizard, Templates, Tutorial, Skip.
 */
const T2TWelcomeOverlay = ({ onChooseWizard, onChooseTemplates, onChooseTutorial, onSkip }) => {
  const { shouldShowWelcome, markWelcomeSeen, logEvent } = useGuidance();

  if (!shouldShowWelcome) return null;

  const handleChoice = (choice, handler) => {
    markWelcomeSeen();
    logEvent("welcome_choice", { choice });
    handler();
  };

  return (
    <div className="t2t-welcome-overlay">
      <div className="t2t-welcome-card">
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔧</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "var(--t2t-text-primary)", margin: 0 }}>
            Welcome to the Workflow Builder
          </h2>
          <p style={{ fontSize: 14, color: "var(--t2t-text-secondary)", marginTop: 8, lineHeight: 1.6 }}>
            Build the approval process for any business task — vendor onboarding, purchase orders,
            expense approvals, and more. No technical knowledge needed.
          </p>
        </div>

        {/* 4 Options Grid */}
        <div className="t2t-welcome-grid">
          <div className="t2t-welcome-option" onClick={() => handleChoice("wizard", onChooseWizard)}>
            <span className="t2t-welcome-icon">🧭</span>
            <div className="t2t-welcome-opt-title">Guide me through it</div>
            <div className="t2t-welcome-opt-desc">
              Step-by-step wizard. I&apos;ll ask you a few questions about your process and build the workflow with you.
            </div>
            <div className="t2t-welcome-opt-tag">Best for first workflow</div>
          </div>

          <div className="t2t-welcome-option" onClick={() => handleChoice("templates", onChooseTemplates)}>
            <span className="t2t-welcome-icon">📋</span>
            <div className="t2t-welcome-opt-title">Start from a template</div>
            <div className="t2t-welcome-opt-desc">
              Pick a ready-made workflow template — Vendor Onboarding, Purchase Approval, Expense Claim — and customise it.
            </div>
            <div className="t2t-welcome-opt-tag">Fastest way to start</div>
          </div>

          <div className="t2t-welcome-option" onClick={() => handleChoice("tutorial", onChooseTutorial)}>
            <span className="t2t-welcome-icon">🎓</span>
            <div className="t2t-welcome-opt-title">Show me an example first</div>
            <div className="t2t-welcome-opt-desc">
              Interactive tutorial — build a sample Expense Approval workflow step by step on the real canvas.
            </div>
            <div className="t2t-welcome-opt-tag">Learn by doing</div>
          </div>

          <div
            className="t2t-welcome-option t2t-welcome-skip"
            onClick={() => handleChoice("skip", onSkip)}
          >
            <span className="t2t-welcome-icon" style={{ opacity: 0.5 }}>🗂</span>
            <div className="t2t-welcome-opt-title" style={{ color: "var(--t2t-text-muted)" }}>
              Skip, I&apos;ll explore myself
            </div>
            <div className="t2t-welcome-opt-desc" style={{ color: "var(--t2t-text-muted)" }}>
              Takes me straight to the blank canvas. I know what I&apos;m doing.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: "var(--t2t-text-muted)" }}>
          ⏱ The guided tour takes about 5 minutes. You can always restart it from the <strong>? Help</strong> button.
        </div>
      </div>
    </div>
  );
};

T2TWelcomeOverlay.propTypes = {
  onChooseWizard: PropTypes.func.isRequired,
  onChooseTemplates: PropTypes.func.isRequired,
  onChooseTutorial: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default T2TWelcomeOverlay;
